import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress
} from "@mui/material";
import {
  DateRange as DateRangeIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Cloud as CloudIcon,
  CalendarToday as CalendarTodayIcon,
  Storage as StorageIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../auth";
import ListLayout from "../../../../../components/Layout/ListLayout";
import {
  useListStorageReports,
  useDownloadStorageReportCsv
} from "../../../../../api/bpm/endpoints";
import { PageStorageUsageReportDto } from "../../../../../api/bpm/model/pageStorageUsageReportDto";
import { StorageUsageReportDto } from "../../../../../api/bpm/model/storageUsageReportDto";
import {
  MONTHS,
  parseCsv,
  formatDateTime,
  downloadCsvFile
} from "../../reports";

export const Route = createFileRoute("/__auth/admin/reports/$reportId/")({
  component: ReportDetailComponent,
});

function ReportDetailComponent() {
  const { reportId } = Route.useParams();
  const idNum = Number(reportId);
  const { t, i18n } = useTranslation();
  const { currentTenant } = useAuth();
  const effectiveTenant = currentTenant || "default-domain";
  const navigate = Route.useNavigate();

  const pageTitle = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <StorageIcon sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem" }, color: "primary.main" }} />
      <span>{t("reports.viewTitle")}</span>
    </Box>
  );

  const getMonthLabel = useCallback(
    (monthVal: number) => {
      const found = MONTHS.find((m) => m.value === monthVal);
      return found ? t(`reports.months.${found.key}`) : String(monthVal);
    },
    [t]
  );

  const { data: apiData, isLoading: isLoadingReports } = useListStorageReports(
    effectiveTenant,
    { page: 0, size: 1000 }
  );

  const reportsData = apiData as PageStorageUsageReportDto | undefined;

  const report = useMemo(() => {
    return reportsData?.content?.find((r) => r.id === idNum);
  }, [reportsData, idNum]);

  const { data: csvResponse, isLoading: isLoadingCsv, error: csvError } = useDownloadStorageReportCsv(
    effectiveTenant,
    idNum,
    undefined,
    { query: { enabled: !isNaN(idNum) } }
  );

  const csvContent = useMemo(() => {
    if (!csvResponse) return null;
    return typeof csvResponse === "string"
      ? csvResponse
      : (csvResponse && typeof csvResponse === "object" && "data" in csvResponse)
        ? (csvResponse as any).data
        : null;
  }, [csvResponse]);

  const parsedCsvData = useMemo(() => {
    if (!csvContent) return null;
    return parseCsv(csvContent);
  }, [csvContent]);

  const environmentVal = useMemo(() => {
    if (!csvContent) return "";

    const csvString = csvContent as string;
    const lines = csvString.split("\n").map((l) => l.trim());

    if (lines.length >= 4) {
      const line4 = lines[3];
      const colonIndex = line4.indexOf(":");

      if (colonIndex !== -1) {
        return line4.substring(colonIndex + 1).trim().replace(/^["']|["']$/g, "");
      }
    }
    return "";
  }, [csvContent]);

  const monthLabel = useMemo(() => {
    if (!report) return "";
    return report.month ? getMonthLabel(report.month) : "";
  }, [report, getMonthLabel]);

  const handleBack = () => {
    navigate({ to: "/admin/reports" });
  };

  const isLoading = isLoadingReports || isLoadingCsv;

  if (isLoading) {
    return (
      <ListLayout
        title={pageTitle}
        subtitle={t("reports.viewSubtitle", { month: "", year: "" })}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </ListLayout>
    );
  }

  if (csvError || !report) {
    return (
      <ListLayout
        title={pageTitle}
        subtitle={t("reports.viewSubtitle", { month: "", year: "" })}
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ borderRadius: 2, textTransform: "none", color: "text.primary", borderColor: "divider" }}
          >
            {t("reports.backButton")}
          </Button>
        }
      >
        <Alert severity="error">
          {csvError ? t("reports.loadDetailError") : t("reports.tableError")}
        </Alert>
      </ListLayout>
    );
  }

  return (
    <ListLayout
      title={pageTitle}
      subtitle={t("reports.viewSubtitle", { month: monthLabel, year: report.year })}
      actions={
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ borderRadius: 2, textTransform: "none", color: "text.primary", borderColor: "divider" }}
        >
          {t("reports.backButton")}
        </Button>
      }
    >

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[1] }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.light", color: "primary.main" }}>
                <CalendarTodayIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">{t("reports.period")}</Typography>
                <Typography variant="body1" fontWeight="bold">{monthLabel}/{report.year}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[1] }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "success.light", color: "success.main" }}>
                <DateRangeIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">{t("reports.generationDate")}</Typography>
                <Typography variant="body1" fontWeight="bold">{formatDateTime(report.createdAt, i18n.language)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[1] }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "warning.light", color: "warning.main" }}>
                <CloudIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary">{t("reports.environment")}</Typography>
                <Typography variant="body1" fontWeight="bold">{environmentVal || effectiveTenant}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[2] }}>
        <CardHeader
          title={t("reports.consolidatedData")}
          titleTypographyProps={{ fontWeight: "bold" }}
          action={
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={() => downloadCsvFile(`relatorio_consumo_${monthLabel}_${report.year}.csv`, csvContent || "")}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("reports.exportCsv")}
            </Button>
          }
        />
        <CardContent>
          {parsedCsvData ? (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    {parsedCsvData.headers.map((header, colIndex) => (
                      <TableCell
                        key={header}
                        align={colIndex === 0 ? "left" : (colIndex === 1 || colIndex === 2) ? "center" : "right"}
                        sx={{ fontWeight: "bold" }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedCsvData.rows.map((row, index) => {
                    const isTotalRow = String(row[parsedCsvData.headers[0] || ""]).toLowerCase().includes("total");
                    return (
                      <TableRow
                        key={index}
                        sx={isTotalRow ? { bgcolor: "action.hover" } : undefined}
                      >
                        {parsedCsvData.headers.map((header, colIndex) => (
                          <TableCell
                            key={header}
                            align={colIndex === 0 ? "left" : (colIndex === 1 || colIndex === 2) ? "center" : "right"}
                            sx={{ fontWeight: isTotalRow ? "bold" : "normal" }}
                          >
                            {row[header]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">{t("reports.tableError")}</Alert>
          )}
        </CardContent>
      </Card>
    </ListLayout>
  );
}
